import type { MouseEventHandler } from "react";
import { useCallback, useMemo, useState, memo } from "react";

import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import type { Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import groupBy from "lodash/groupBy";
import isString from "lodash/isString";
import { Trans, useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import { Modal, useAdvancedToggle } from "@waterly/frontend-components";
import type { DropdownOption } from "@waterly/frontend-components";

import {
  namedOperations,
  useAddSystemFavoriteMutation,
  useDeleteSystemFavoriteMutation,
} from "../../api/generated/graphql";
import type { SystemTypeEnum } from "../../api/generated/graphql";
import { useLoggedInUserDisplayName } from "../../hooks/useLoggedInUser";
import { useWaterlyAppStore } from "../../stores";
import { stateFullNames } from "../../util/stateUtil";
import Card from "../shared/Card/Card";

import type { DesktopSystemTableProps } from "./DesktopSystemTable";
import DesktopSystemTable from "./DesktopSystemTable";
import MobileSystemTable from "./MobileSystemTable";
import type { SystemsToolbarProps } from "./SystemsToolbar";
import SystemsToolbar from "./SystemsToolbar";

import type { OverviewSystem } from "./index";

export const ALL_STATES_ID = "ALL_STATES";

const getSystemRegion = (system: OverviewSystem): string => system.addresses?.[0]?.region ?? "";

type SystemTableProps = {
  isLoading?: boolean;
  systems: OverviewSystem[];
};

const SystemTable = ({ systems, isLoading = false }: SystemTableProps) => {
  const { t } = useTranslation(["common", "SystemTypeEnum"]);
  const loggedInUserDisplayName = useLoggedInUserDisplayName();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [addSystem] = useAddSystemFavoriteMutation({
    refetchQueries: [namedOperations.Query.GetSystemsWithOperator],
  });
  const [deleteSystem] = useDeleteSystemFavoriteMutation({
    refetchQueries: [namedOperations.Query.GetSystemsWithOperator],
  });

  const ALL_STATES_OPTION = useMemo(
    () => ({ id: ALL_STATES_ID, displayName: t("common:all_states") } as const),
    [t],
  );

  const {
    overviewFavoritesFilter,
    toggleOverviewFavoritesFilter,
    overviewStateFilter,
    setOverviewStateFilter,
  } = useWaterlyAppStore(
    useShallow((s) => ({
      overviewFavoritesFilter: s.overviewFavoritesFilter,
      overviewStateFilter: s.overviewStateFilter,
      toggleOverviewFavoritesFilter: s.toggleOverviewFavoritesFilter,
      setOverviewStateFilter: s.setOverviewStateFilter,
    })),
  );

  const [
    isRequestAccessModalOpen,
    { toggleFalse: closeRequestAccessModal, toggleTrue: openRequestAccessModal },
  ] = useAdvancedToggle(false);
  const [selectedSystemName, setSelectedSystemName] = useState("");

  const systemsGroupedByRegion = useMemo(
    () => groupBy(systems, (system) => getSystemRegion(system)),
    [systems],
  );

  const options = useMemo(() => {
    const opts = Object.keys(systemsGroupedByRegion)
      .filter(Boolean)
      .sort()
      .map((value) => ({
        id: value,
        displayName: stateFullNames[value] ?? value,
      }));
    opts.unshift(ALL_STATES_OPTION);
    return opts;
  }, [systemsGroupedByRegion, ALL_STATES_OPTION]);

  const stateFilter = useMemo<DropdownOption>(() => {
    if (isString(overviewStateFilter)) {
      return options.find((option) => option.id === overviewStateFilter) ?? ALL_STATES_OPTION;
    }
    return overviewStateFilter;
  }, [ALL_STATES_OPTION, options, overviewStateFilter]);

  const systemsFiltered = useMemo(
    () =>
      systems.filter((system) => {
        if (overviewFavoritesFilter && !system.isFavorite) return false;
        if (stateFilter.id === ALL_STATES_OPTION.id) return true;
        return stateFilter.id === getSystemRegion(system);
      }),
    [systems, overviewFavoritesFilter, stateFilter.id, ALL_STATES_OPTION.id],
  );

  const toggleFavorite: MouseEventHandler = useCallback(
    (event) => {
      const systemId = Number(event.currentTarget.getAttribute("data-entity-id"));
      const isFavorite = event.currentTarget.getAttribute("data-entity-is-favorite") === "true";
      const options = { variables: { input: { id: systemId } } };
      isFavorite ? void deleteSystem(options) : void addSystem(options);
    },
    [addSystem, deleteSystem],
  );

  const handleSystemClicked: DesktopSystemTableProps["onSystemClicked"] = useCallback(
    (systemName: string) => {
      openRequestAccessModal();
      setSelectedSystemName(systemName);
    },
    [openRequestAccessModal],
  );

  const handleChangeStateFilter = useCallback<SystemsToolbarProps["onChangeStateFilter"]>(
    (_event, value) => setOverviewStateFilter(value),
    [setOverviewStateFilter],
  );

  const handleChangeFavoritesFilter = useCallback(() => {
    toggleOverviewFavoritesFilter();
    setOverviewStateFilter(ALL_STATES_OPTION);
  }, [ALL_STATES_OPTION, setOverviewStateFilter, toggleOverviewFavoritesFilter]);

  const getEmailLinkHrefValue = useMemo(() => {
    const emailLinkHrefValue = ["mailto:support@waterlysoftware.com"];
    if (loggedInUserDisplayName) {
      const emailSubject = `?subject=[Access Request] ${selectedSystemName} for ${loggedInUserDisplayName}`;
      const emailBody = `&body=${loggedInUserDisplayName} is requesting access to the ${selectedSystemName} system.`;
      emailLinkHrefValue.push(emailSubject);
      emailLinkHrefValue.push(emailBody);
    } else {
      const emailSubject = `?subject=[Access Request] ${selectedSystemName} for <Enter your name and email>`;
      const emailBody = `&body=<Enter your name and email> is requesting access to the ${selectedSystemName} system.`;
      emailLinkHrefValue.push(emailSubject);
      emailLinkHrefValue.push(emailBody);
    }
    return emailLinkHrefValue.join("");
  }, [selectedSystemName, loggedInUserDisplayName]);

  const getSystemType = useCallback(
    (type: SystemTypeEnum | string | null | undefined): string => {
      if (type === null || type === undefined || type === "Unknown") return t("common:no_type");
      return t(`SystemTypeEnum:${type}`);
    },
    [t],
  );

  const closeNoAccessModal = useCallback(() => {
    closeRequestAccessModal();
    setSelectedSystemName("");
  }, [closeRequestAccessModal]);

  return (
    <Card title={t("common:systems_table")}>
      <Grid container justifyContent="center">
        <SystemsToolbar
          onChangeStateFilter={handleChangeStateFilter}
          stateFilterOptions={options}
          stateFilterValue={stateFilter}
          filterFavorites={overviewFavoritesFilter}
          onChangeFilterFavorites={handleChangeFavoritesFilter}
        />
        <Grid container size={{ xs: 12 }} sx={{ xs: { maxHeight: "100px" } }}>
          {isMobile && (
            <MobileSystemTable
              systems={systemsFiltered}
              isLoading={isLoading}
              onSystemClicked={handleSystemClicked}
            />
          )}
          {!isMobile && (
            <DesktopSystemTable
              systems={systemsFiltered}
              isLoading={isLoading}
              onSystemClicked={handleSystemClicked}
              getSystemType={getSystemType}
              onToggleFavorite={toggleFavorite}
            />
          )}
        </Grid>
        <Modal
          open={isRequestAccessModalOpen}
          title={t("common:additional_permissions_needed_title")}
          actions={
            <Button variant="contained" onClick={closeNoAccessModal}>
              {t("common:ok")}
            </Button>
          }
        >
          <Typography>
            <Trans
              i18nKey="common:additional_permissions_needed_message"
              t={t}
              components={{
                helpLink: <Link href={getEmailLinkHrefValue} target="_new" />,
              }}
            />
          </Typography>
        </Modal>
      </Grid>
    </Card>
  );
};

export default memo(SystemTable);
